import { useRef } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getAccountKycAml } from '@arrived/api_v2';
import { AccountId, AccountKycAml, KycAmlRollupStatus } from '@arrived/models';

import { UseQueryOptions, UseQueryResult } from '../../queries.utils';
import { accountKycAmlKeyFn } from './accountKycAml.keys';

/**
 * Since we don't have a way to push updates from the BE when a users KYC status updates, the asynchronous nature of KYC
 * requires us to poll the BE for KYC AML data when we're in a PENDING_SYNC state. By default this will poll the BE
 * in intervals of 250ms * 2 ^ n where "n" is the number of refetches that have already happened.
 */
export function useGetAccountKycAmlQuery(
  accountId?: AccountId,
  options?: UseQueryOptions<AccountKycAml>
): UseQueryResult<AccountKycAml> {
  // Exponential backoff and refetch counter, will be undefined when we are not refetching and a defined value (0 or
  // greater) when we are.
  const refetchCount = useRef<number>();

  return useQuery(
    accountKycAmlKeyFn(accountId!),
    () => {
      // If we are refetching, increment the counter.
      if (refetchCount.current != null) {
        refetchCount.current += 1;
      }
      return getAccountKycAml(accountId!);
    },
    {
      ...options,
      enabled: (options?.enabled ?? true) && accountId != null,
      refetchInterval:
        options?.refetchInterval ??
        (() => {
          return refetchCount.current != null ? 250 * Math.pow(2, refetchCount.current) : false;
        }),
      onSuccess: (data) => {
        options?.onSuccess?.(data);

        // If the rollup status is PENDING_SYNC, we want to start refetching on an exponential backoff driven interval,
        // so we set the refetchCount ref to have a value of 0.
        if (data.kycAmlRollupStatus === KycAmlRollupStatus.PENDING_SYNC) {
          if (refetchCount.current == null) {
            refetchCount.current = 0;
          }
        } else {
          refetchCount.current = undefined;
        }
      },
    }
  );
}
